import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock } from "vue"

import FilePicker from '@grantstreet/psc-vue/components/file-picker/FilePicker.vue'
import FileDetails from '@grantstreet/psc-vue/components/file-picker/FileDetails.ts'
import UploadApiClass from './upload-api.ts'
import Upload from './models/Upload.ts'
import type { FilePickerProps } from '@grantstreet/psc-vue/components/file-picker/component-props.ts'
import { Ref, ref, watch } from 'vue'
import type { SentryMessageFunction } from '@grantstreet/sentry'


export default /*#__PURE__*/_defineComponent({
  __name: 'UploadWidget',
  props: {
    id: {},
    name: {},
    disabled: { type: Boolean },
    fileTypes: {},
    maxFileSize: {},
    multiple: { type: Boolean },
    sourceTag: { default: 'direct-charge-create-jurisdiction' },
    retainUntil: {},
    exceptionLogger: {}
  },
  emits: ["update:modelValue", "update:isProcessing"],
  setup(__props: any, { emit }) {

const props = __props;

/**
 * This is a wrapper around the file picker component that automatically uploads
 * any selected files to the PSP Uploads Service.
 *
 * Note, this widget does not support pre-loading an initial value at this
 * point. That would require retrieving the file name/size before rendering the
 * widget, which is currently only an m2m action, and we do not have a use case
 * for restoring form state yet. We could add that in the future.
 */




const uploadApi = new UploadApiClass({ exceptionLogger: props.exceptionLogger })
const fileUploadList: Ref<{
  upload: Upload
  // This has to include the original file object so we can potentially identify
  // and remove the file later
  fileDetails: FileDetails
}[]> = ref([])

// Emit the new input value (a list of custom file objects) when the upload list
// changes
watch(fileUploadList, newUploads => {
  emit('update:modelValue', newUploads.map(fileUpload => fileUpload.upload))
})

/**
 * Handles a file addition by uploading the file to the server via the uploads
 * service
 */
async function uploadFile (fileDetails: FileDetails) {
  try {
    // Retrieve an upload ID and URL
    const {
      data: {
        uploadUri,
        uploadId,
      },
    } = await uploadApi.initiateUpload(props.sourceTag, props.retainUntil)

    if (!fileDetails.file) {
      return
    }

    // Upload the file to the URL
    await uploadApi.makeUpload(uploadUri, fileDetails.file)

    fileUploadList.value.push({
      upload: new Upload({
        // Prefix all upload IDs with 'uploads-service:' to make it clear where
        // these UUIDs come from
        id: `uploads-service:${uploadId}`,
        name: fileDetails.name,
        sizeBytes: fileDetails.sizeBytes,
        mimeType: fileDetails.mimeType,
      }),
      fileDetails,
    })
  }
  catch (error) {
    props.exceptionLogger(error as Error)
    throw new Error(`There was an error adding file '${fileDetails.name}'. Please try again.`)
  }
}

/**
 * Handle removing uploads when the file list changes.
 */
function updateValue (newFiles: FileDetails[]) {
  for (const existingUpload of fileUploadList.value) {
    // If this upload no longer exists in the internal file list, remove it from
    // the upload list
    if (!newFiles.includes(existingUpload.fileDetails)) {
      const index = fileUploadList.value.findIndex(
        upload => upload.fileDetails === existingUpload.fileDetails,
      )
      if (index !== -1) {
        fileUploadList.value.splice(index, 1)
      }
    }
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(FilePicker, {
    id: props.id,
    name: props.name,
    disabled: props.disabled,
    "file-types": props.fileTypes,
    "max-file-size": props.maxFileSize,
    multiple: props.multiple,
    "process-file": uploadFile,
    "onUpdate:modelValue": updateValue,
    "onUpdate:isProcessing": _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('update:isProcessing', $event)))
  }, null, 8, ["id", "name", "disabled", "file-types", "max-file-size", "multiple"]))
}
}

})